import { Component, OnInit } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
import { AuthService } from './shared/services/auth.service';
import { BackendService } from './shared/services/backend.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'wg-team-ui';

  constructor(
    private cookieService: CookieService,
    private authService: AuthService,
    private backendService: BackendService
  ) {}

  ngOnInit() {
    // FIX: Clean up legacy MSAL cookies on app init to prevent RequestHeaderSectionTooLarge error
    // This migrates existing sessions from cookies to sessionStorage
    this.cleanupMsalCookies();

    // Re-fetch /static on every app bootstrap (e.g. a page refresh) so Config Portal
    // changes — new teams, locations, shift timings, permissions — are picked up without
    // requiring a re-login. Only when already authenticated; the login flow fetches static
    // for fresh sessions. The call is cache-busted and swallows errors (see refreshStaticData).
    this.refreshStaticDataIfLoggedIn();

    // Monitor cookie size in development mode
    if (!environment.production) {
      this.monitorCookieSize();
    }
  }

  /**
   * Refreshes the cached static data when the user already has a valid session, so a plain
   * page refresh surfaces the latest config. No-op on the login page (no session yet).
   */
  private refreshStaticDataIfLoggedIn(): void {
    if (this.authService.isLoggedIn()) {
      this.backendService.refreshStaticData().subscribe();
    }
  }

  /**
   * Removes legacy MSAL cookies to prevent RequestHeaderSectionTooLarge error
   * MSAL tokens are now stored in sessionStorage instead of cookies
   */
  private cleanupMsalCookies(): void {
    try {
      const msalCookies = ['msal-access-token', 'msal-id-token'];
      const domain = this.getDomainFromURL(window.location.hostname);
      
      msalCookies.forEach(cookieName => {
        if (this.cookieService.check(cookieName)) {
          console.log(`Cleaning up legacy MSAL cookie: ${cookieName}`);
          
          // Delete cookie from current domain
          this.cookieService.delete(cookieName, '/');
          
          // Delete cookie from parent domain
          this.cookieService.delete(cookieName, '/', domain);
          
          // Delete cookie from root domain
          this.cookieService.delete(cookieName, '/', '');
        }
      });
    } catch (error) {
      console.error('Error cleaning up MSAL cookies:', error);
    }
  }

  /**
   * Monitors total cookie size to prevent hitting CloudFront's 8KB limit
   * Development mode only
   */
  private monitorCookieSize(): void {
    try {
      const allCookies = document.cookie;
      const cookieSize = new Blob([allCookies]).size;
      const cookieSizeKB = (cookieSize / 1024).toFixed(2);
      
      console.log(`Total cookie size: ${cookieSizeKB} KB`);
      
      if (cookieSize > 6144) { // Warn at 6KB (threshold before 8KB limit)
        console.warn(`⚠️ Cookie size (${cookieSizeKB} KB) is approaching CloudFront's 8KB limit!`);
      } else {
        console.log(`✓ Cookie size is within safe limits (< 6 KB threshold)`);
      }
    } catch (error) {
      console.error('Error monitoring cookie size:', error);
    }
  }

  /**
   * Extracts domain from hostname for cookie operations
   */
  private getDomainFromURL(hostname: string): string {
    const parts = hostname.split('.');
    if (parts.length > 2) {
      return '.' + parts.slice(-2).join('.');
    }
    return hostname;
  }
}
